// De "te plannen"-bak: taken die nog geen persoon en dag hebben, één rij per project, op
// hetzelfde raster als het rooster eronder — sleep een taak op een persoon en dag, of een
// deliverable voor alles in één keer. Stond vroeger bovenin het Rooster; sinds 25 sep
// (Joris: "splitsen en hergebruiken bij Nog in te plannen") alleen op Nog in te plannen.
import React, { useMemo, useState } from 'react';
import { Box, Typography, Tooltip, IconButton } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, Close as CloseIcon } from '@mui/icons-material';
import { effectiveTaskHours, DEFAULT_CAPACITY_RULES } from '../../utils/capacityCalc';
import { bakBalken, fmtUren } from './rooster';
import { BakRij, DeliverableChip, NAAM_BREEDTE } from './RoosterOnderdelen';

const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';
const GEEN = [];

/** Bakgroepen → per project, gefilterd op project(en), deliverables en zoekterm. Puur. */
export function bakPerProject(unassignedTasks, { projectIds = [], deliverableIds = [], zoek = '' } = {}) {
  const zoektTaak = !!zoek && Object.values(unassignedTasks).some(g =>
    (g.tasks || []).some(t => `${t.name} ${g.projectName}`.toLowerCase().includes(zoek)));
  const perProject = {};
  Object.values(unassignedTasks).forEach((group) => {
    if (projectIds.length && !projectIds.includes(group.projectId)) return;
    const taken = (group.tasks || []).filter((t) => {
      if (deliverableIds.length && !deliverableIds.includes(t.deliverableId)) return false;
      return !zoektTaak || `${t.name} ${group.projectName}`.toLowerCase().includes(zoek);
    });
    if (!taken.length) return;
    const key = group.projectId || group.projectName;
    if (!perProject[key]) perProject[key] = { key, projectId: group.projectId, projectName: group.projectName, tasks: [] };
    perProject[key].tasks.push(...taken);
  });
  return { projecten: Object.values(perProject), zoektTaak };
}

function deliverablesVan(unassignedTasks, projectIds) {
  const kaart = {};
  Object.values(unassignedTasks).forEach((group) => {
    if (projectIds.length && !projectIds.includes(group.projectId)) return;
    (group.tasks || []).forEach((t) => {
      if (!t.deliverableId) return;
      if (!kaart[t.deliverableId]) kaart[t.deliverableId] = { id: t.deliverableId, name: t.deliverableName || 'Deliverable', taskCount: 0, tasks: [] };
      kaart[t.deliverableId].taskCount += 1;
      kaart[t.deliverableId].tasks.push(t);
    });
  });
  return Object.values(kaart).sort((a, b) => b.taskCount - a.taskCount);
}

/**
 * @param projectIds     alleen deze projecten (uit de lijst erboven gekozen); leeg = alles
 * @param projectLabel   naam van het gekozen project, voor de kop
 * @param onWisProject   filter opheffen
 * @param onTaakKlik     klik op een taak (snel toewijzen aan de geselecteerde persoon)
 * @param leegProject    wat de bak zegt als het gekozen project er geen taken in heeft
 *                       (bijv. "96 u montage is nog geen taak · taken aanmaken"); standaard
 *                       "alles heeft een persoon en dag"
 */
export default function TePlannenBak({
  rooster, projectIds = GEEN, projectLabel, onWisProject, zoek = '', selectedPersonName = '', onTaakKlik, leegProject,
}) {
  const { unassignedTasks, kolommen, sjabloon, rules } = rooster;
  const [open, setOpen] = useState(true);
  const [deliverableFilter, setDeliverableFilter] = useState([]);

  const deliverables = useMemo(() => (projectIds.length ? deliverablesVan(unassignedTasks, projectIds) : []), [unassignedTasks, projectIds]);
  const geldigeDlv = useMemo(() => deliverableFilter.filter(id => deliverables.some(d => d.id === id)), [deliverableFilter, deliverables]);
  const { projecten, zoektTaak } = useMemo(
    () => bakPerProject(unassignedTasks, { projectIds, deliverableIds: geldigeDlv, zoek }),
    [unassignedTasks, projectIds, geldigeDlv, zoek],
  );
  const rijen = useMemo(() => projecten.map(p => ({ ...p, plaatsing: bakBalken(p.tasks, kolommen, rules) })), [projecten, kolommen, rules]);
  const aantal = projecten.reduce((s, p) => s + p.tasks.length, 0);
  const uren = projecten.reduce((s, p) => s + p.tasks.reduce((a, t) => a + effectiveTaskHours(t, DEFAULT_CAPACITY_RULES).val, 0), 0);
  const hint = selectedPersonName ? `Klik een taak om hem aan ${selectedPersonName} te geven` : 'Sleep een taak naar een persoon en dag';

  return (
    <Box sx={{ flexShrink: 0, borderBottom: '1px solid #d9dde3', bgcolor: '#fff' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.5, minWidth: 0, borderBottom: open ? '1px solid #eceef1' : 'none' }}>
        <Box onClick={() => setOpen(o => !o)} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', flexShrink: 0 }}>
          {open ? <ExpandLessIcon sx={{ fontSize: 16, color: GEDEMPT }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: GEDEMPT }} />}
          <Tooltip title="Sleep een taak op een persoon en dag · sleep een deliverable om alles in één keer in te passen · selecteer eerst een persoon en klik een taak om hem direct toe te wijzen">
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEKST }}>Te plannen</Typography>
          </Tooltip>
          <Typography sx={{ fontSize: 11, color: GEDEMPT }}>{aantal} · {fmtUren(uren)}u</Typography>
        </Box>
        {projectLabel && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0, flexShrink: 1 }}>
            <Typography noWrap sx={{ fontSize: 12, color: TEKST, minWidth: 0 }}>· {projectLabel}</Typography>
            {onWisProject && (
              <Tooltip title="Alle projecten tonen">
                <IconButton size="small" aria-label="Alle projecten tonen" onClick={onWisProject} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 14 }} /></IconButton>
              </Tooltip>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', overflowX: 'auto', minWidth: 0, flex: 1, py: 0.25, '&::-webkit-scrollbar': { height: 4 } }}>
          {deliverables.slice(0, 24).map((dlv) => (
            <Box key={dlv.id} sx={{ flexShrink: 0 }}>
              <DeliverableChip
                dlv={dlv}
                selected={geldigeDlv.includes(dlv.id)}
                tasks={dlv.tasks}
                onClick={() => setDeliverableFilter((prev) => (prev.includes(dlv.id) ? prev.filter((id) => id !== dlv.id) : [...prev, dlv.id]))}
              />
            </Box>
          ))}
        </Box>
        {open && aantal > 0 && (
          <Typography noWrap sx={{ fontSize: 11, color: GEDEMPT, flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>{hint}</Typography>
        )}
      </Box>
      {open && (
        <Box sx={{ overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable', maxHeight: '26vh' }}>
          {rijen.length === 0 ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: `${NAAM_BREEDTE}px 1fr` }}>
              <Box sx={{ borderRight: '1px solid #e2e8f0' }} />
              {projectLabel && leegProject && !geldigeDlv.length && !zoektTaak ? (
                <Box sx={{ p: 1.25, fontSize: 12, color: GEDEMPT }}>{leegProject}</Box>
              ) : (
                <Typography sx={{ p: 1.25, fontSize: 12, color: GEDEMPT }}>
                  {projectLabel ? 'Geen taken van dit project in de bak — alles heeft een persoon en dag.'
                    : geldigeDlv.length || zoektTaak ? 'Geen taken binnen dit filter.' : 'Alles is ingepland — geen taken meer in de bak.'}
                </Typography>
              )}
            </Box>
          ) : rijen.map(p => (
            <BakRij
              key={p.key}
              project={p}
              kolommen={kolommen}
              sjabloon={sjabloon}
              plaatsing={p.plaatsing}
              onTaakKlik={onTaakKlik}
              klikHint={onTaakKlik ? `Klik om toe te wijzen aan ${selectedPersonName}` : 'Sleep naar een persoon en dag'}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
